// PayGate.to client-side configuration
// Hardcoded because Next.js 16 Turbopack doesn't inline NEXT_PUBLIC_* in client bundles
import { isPayTreeMethod } from "@/config/paytree";
const PAYGATETO_ENABLED = true;
const PAYGATETO_ENABLED_CHANNELS = ["eu", "us"];
const PAYGATETO_GATEWAY_ID = "QXBwOjQ=";

/**
 * Fiat checkout presentation. Keep the deployed default on the proven direct
 * provider picker until the production pilot is explicitly started. This is a
 * deliberately code-controlled switch: Turbopack does not reliably expose
 * NEXT_PUBLIC_* runtime settings to the client bundle, and the agreed rollback
 * path is a small Codex/Claude change followed by a deploy.
 */
export type PayGateFiatCheckoutMode = "legacy_direct" | "multi_provider";
export type PaymentExperience = PayGateFiatCheckoutMode;

export const PAYGATETO_MULTI_PROVIDER_PAYMENT_METHOD = "paygate_multi_provider";
const PAYGATETO_FIAT_CHECKOUT_MODE: PayGateFiatCheckoutMode = "multi_provider";

export function getPayGateToFiatCheckoutMode(): PayGateFiatCheckoutMode {
	return PAYGATETO_FIAT_CHECKOUT_MODE;
}

export function isPayGatePaymentExperience(value: unknown): value is PaymentExperience {
	return value === "legacy_direct" || value === "multi_provider";
}

export function isPayGateMultiProviderMethod(paymentMethod: unknown): boolean {
	return paymentMethod === PAYGATETO_MULTI_PROVIDER_PAYMENT_METHOD;
}

/**
 * The `payment_experience` value the CLIENT reports for a chosen method, matching
 * what the transaction-initialize route derives server-side. It depends only on the
 * chosen method, not the fiat checkout mode: the hosted multi-provider page is itself
 * selected via the multi-provider sentinel method, and every other fiat method is a
 * direct on-ramp.
 *
 * Previously the client attached `fiatCheckoutMode` (= "multi_provider") to every
 * fiat method's metadata + PostHog events, even for direct on-ramps (Revolut tile),
 * so the funnel could not separate the direct journey from the hosted multi-provider
 * page. The server already computes "legacy_direct" for those — this keeps the client
 * in sync. Crypto has no on-ramp experience, and neither does PayTree: it is an
 * orchestrator with its own hosted page, and transaction-initialize likewise derives
 * `undefined` for it. Reporting "legacy_direct" here would tag every PayTree redirect
 * as a direct PayGate on-ramp in the funnel.
 */
export function resolvePaymentExperienceForMethod(paymentMethod: unknown): PaymentExperience | undefined {
	if (paymentMethod === "crypto") return undefined;
	if (isPayTreeMethod(paymentMethod)) return undefined;
	if (isPayGateMultiProviderMethod(paymentMethod)) return "multi_provider";
	return "legacy_direct";
}

/**
 * The default payment method for the selector — used both by the initial-state
 * initializer and by the guard that resets an unavailable direct method (e.g. the
 * Revolut tile disappearing when a points redemption drops the total below its
 * minimum).
 *
 * PayTree wins when visible: it holds position 1 and the "Recommended" badge, and
 * the preselection has to agree with them, otherwise the badge sits on a tile the
 * buyer still has to click. Then SellAbroad (inline, best-converting); otherwise
 * crypto, which is always available (the legacy_direct catalog can't be assumed
 * here, and PaymentMethodSelector no longer renders a tile for the hosted
 * multi-provider option at all - see the note on PAYGATETO_MULTI_PROVIDER_PAYMENT_METHOD
 * usage above).
 *
 * Used to fall through to the multi-provider sentinel here when
 * `fiatCheckoutMode === "multi_provider"`. Task 9 (2026-08-22) removed that
 * option's only tile from PaymentMethodSelector - the "Weitere Zahlungsmethoden"
 * entry point pointed at PayGate's own hosted checkout, whose `creditcard`
 * endpoint is documented elsewhere in this repo as answering 400 (216 redirects,
 * 1 completed order over 90 days). Returning the sentinel from here (which the
 * Revolut-availability guard in payment-step.tsx calls) meant this function
 * could hand back a method with no visible tile: nothing highlighted in the UI,
 * pay button still enabled, submit reaching the dead endpoint. Crypto is the
 * next method that always renders, so it is now the unconditional final
 * fallback.
 */
export function resolveDefaultPaymentMethod(params: {
	sellabroadEnabled: boolean;
	fiatCheckoutMode: PayGateFiatCheckoutMode;
	/** First visible PayTree method, or null/undefined when PayTree is off. */
	paytreeMethodId?: string | null;
}): string {
	if (params.paytreeMethodId) return params.paytreeMethodId;
	if (params.sellabroadEnabled) return "sellabroad";
	return "crypto";
}

/**
 * Resume-payment flow ("Zahlung fortsetzen") feature flag — DEFAULT OFF.
 *
 * Hardcoded constant, NOT an env var. The previous
 * `process.env.NEXT_PUBLIC_PAYGATETO_RESUME_ENABLED === "true"` was a permanent no-op:
 * Next.js 16 Turbopack does not inline NEXT_PUBLIC_* into the checkout client bundle
 * (the same reason PAYGATETO_ENABLED above is hardcoded), and the var was never even
 * passed as a docker build-arg — so the value was always `undefined` → `false` and the
 * resume button never rendered, regardless of any env setting. To flip the global
 * default, edit THIS line.
 *
 * MUST stay `false` until PayGate.to confirms that re-opening a wallet's
 * `process-payment.php?address=...` does NOT accept a SECOND on-chain deposit
 * (the double-charge gate, Q1). With the flag off the resume button never renders and
 * the fallback is a fresh `transactionInitialize` (a new wallet — always safe).
 *
 * Per-session runtime opt-in for testing WITHOUT a redeploy: `?pgresume=1` enables resume
 * for that one checkout session; `?pgresume=0` is the kill switch. See `resolveResumeEnabled`
 * in src/checkout/lib/utils/url.ts.
 */
export const PAYGATETO_RESUME_ENABLED = false;

export function isPayGateToEnabledForChannel(channel: string): boolean {
	if (!PAYGATETO_ENABLED) return false;
	return PAYGATETO_ENABLED_CHANNELS.includes("*") || PAYGATETO_ENABLED_CHANNELS.includes(channel);
}

export function getPayGateToGatewayId(): string {
	return PAYGATETO_GATEWAY_ID;
}

/**
 * Fallback-Land fuer PayGates `PGTO-IPCountry`, abgeleitet aus dem Kanal.
 *
 * PayGate baut die Anbieterliste der hosted Zahlseite nach dem Land des Kaeufers.
 * Normalerweise liefert Cloudflare das (`request.cf.country` im White-Label-Worker);
 * faellt das aus, ging bisher `XX` raus, und PayGate antwortet darauf mit einer
 * reduzierten Liste OHNE revolut, stripe, coinbase, banxa, blockchaincom und transak
 * (gemessen 2026-08-19). Dieser Wert reist als Query-Parameter `pgto_cc` mit und wird
 * vom Worker konsumiert und entfernt, bevor die Anfrage PayGate erreicht.
 */
export function resolvePayGateCountryHint(channel: string | undefined): string {
	return channel === "us" ? "US" : "DE";
}

/**
 * Branding der hosted Zahlseite (`pay.php`). Reine Query-Parameter, live verifiziert.
 * Werte gespiegelt aus src/styles/brand.css: `--background` (oklch(0.985 0.001 260))
 * und `--primary` (oklch(0.36 0.11 258)).
 *
 * ACHTUNG: die URL wird in client.ts per String-Konkatenation gebaut (weil `address_in`
 * bereits kodiert ankommt). Ein rohes `#` macht alles danach zum URL-Fragment und die
 * Parameter verschwinden lautlos - daher werden sie dort encodiert.
 */
export const PAYGATETO_CHECKOUT_BRANDING = {
	logo: "https://bergdorfbio.com/logo.png",
	background: "#fafafb",
	/** Faerbt Betragsbanner UND Button; PayGates Default ist ein gruener Verlauf. */
	theme: "#123b75",
	button: "#123b75",
} as const;

function hostnameFromCheckoutUrl(checkoutUrl: string): string {
	try {
		return new URL(checkoutUrl).hostname;
	} catch {
		return "pay.bergdorfbio.com";
	}
}

export function getPayGateToServerConfig() {
	const checkoutUrl = process.env.PAYGATETO_CHECKOUT_URL ?? "https://pay.bergdorfbio.com";
	return {
		walletAddress: process.env.PAYGATETO_WALLET_ADDRESS ?? "",
		appToken: process.env.SALEOR_PAYGATETO_APP_TOKEN ?? "",
		apiUrl: process.env.PAYGATETO_API_URL ?? "https://gateway.bergdorfbio.com/control",
		checkoutUrl,
		/** Host für PayGate-Parameter `domain` (White-Label), wie beim Cloudflare-Worker */
		checkoutDomainHost: hostnameFromCheckoutUrl(checkoutUrl),
	};
}
